import { useState, useMemo } from 'react';
import { Table } from 'reactstrap';
import { useAuth } from 'react-oidc-context';
import { useAuditLog, downloadAuditCsv } from '../../api/platformAudit';
import { useTenantAuditLog, downloadTenantAuditCsv, type TenantAuditFilters } from '../../api/tenantAudit';
import { useTenantContext } from '../../auth/useTenantContext';
import { AuditFilters, type FilterState } from './AuditFilters';
import { AuditRow } from './AuditRow';

interface Tenant { id: number; label: string; }
interface Actor  { id: number; label: string; }

interface Props {
  scope?: { tenantId?: number };
  availableTenants: Tenant[];
  availableActors: Actor[];
  apiBase?: 'platform' | 'tenant';
  hideAdvanced?: boolean;
  initialScope?: { entity_types?: string[]; entity_id?: string };
}

const initialFilters: FilterState = {
  datePreset: 'last_24h', from: null, to: null,
  tenantIds: [], actorUserIds: [],
  actions: [], entityTypes: [],
  entityId: '', payloadSearch: '',
};

function presetToRange(p: FilterState['datePreset']): { from?: string; to?: string } {
  const now = new Date();
  const to = now.toISOString();
  if (p === 'last_24h') return { from: new Date(now.getTime() - 24 * 3600 * 1000).toISOString(), to };
  if (p === 'last_7d') return { from: new Date(now.getTime() - 7 * 24 * 3600 * 1000).toISOString(), to };
  if (p === 'last_30d') return { from: new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString(), to };
  return {};
}

export function AuditLogTable({ scope, availableTenants, availableActors, apiBase = 'platform', hideAdvanced, initialScope }: Props) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const tenantCtx = useTenantContext();

  const seedFilters: FilterState = {
    ...initialFilters,
    entityTypes: initialScope?.entity_types ?? initialFilters.entityTypes,
    entityId:    initialScope?.entity_id    ?? initialFilters.entityId,
  };
  const [filters, setFilters] = useState<FilterState>(seedFilters);
  const [cursor, setCursor] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const showTenantColumn = apiBase === 'platform' && !scope?.tenantId;

  const apiFilters = useMemo(() => {
    const r = presetToRange(filters.datePreset);
    return {
      ...r,
      from: filters.datePreset === 'custom' ? filters.from ?? undefined : r.from,
      to: filters.datePreset === 'custom' ? filters.to ?? undefined : r.to,
      tenant_ids: filters.tenantIds,
      actor_user_ids: filters.actorUserIds,
      actions: filters.actions,
      entity_types: filters.entityTypes,
      entity_id: filters.entityId || undefined,
      payload_search: filters.payloadSearch || undefined,
      cursor: cursor ?? undefined,
      limit: 50,
      tenant_scope_id: scope?.tenantId,
    };
  }, [filters, cursor, scope?.tenantId]);

  const platformQuery = useAuditLog(apiBase === 'platform' ? token : null, apiFilters);
  const tenantQuery   = useTenantAuditLog(
    apiBase === 'tenant' ? token : null,
    apiBase === 'tenant' ? tenantCtx.tenantCode : null,
    apiFilters as TenantAuditFilters,
  );
  const data      = apiBase === 'tenant' ? tenantQuery.data      : platformQuery.data;
  const isLoading = apiBase === 'tenant' ? tenantQuery.isLoading : platformQuery.isLoading;

  const onCsv = async () => {
    if (!token) return;
    if (apiBase === 'tenant') {
      if (!tenantCtx.tenantCode) return;
      await downloadTenantAuditCsv(token, tenantCtx.tenantCode, apiFilters as TenantAuditFilters);
    } else {
      await downloadAuditCsv(apiFilters, token);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <AuditFilters
          value={filters}
          onChange={(next) => { setFilters(next); setCursor(null); setExpandedId(null); }}
          availableTenants={availableTenants}
          availableActors={availableActors}
          hideTenantFilter={!showTenantColumn}
          hideAdvanced={hideAdvanced}
        />
        <button type="button" className="btn btn-sm btn-ghost-secondary" onClick={onCsv}>↓ Export CSV</button>
      </div>

      {isLoading ? <p>Caricamento…</p> : (
        <div className="table-responsive">
        <Table className="table-hover align-middle table-striped mb-0 mt-3">
          <thead>
            <tr>
              <th>Time</th>
              {showTenantColumn && <th>Tenant</th>}
              <th>Actor</th>
              <th>Action</th>
              <th>Entity</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.data.length === 0 && (
              <tr><td colSpan={showTenantColumn ? 6 : 5} style={{ textAlign: 'center', padding: 24 }}>
                Nessun evento audit nei filtri selezionati.
              </td></tr>
            )}
            {data?.data.map((entry) => (
              <AuditRow
                key={entry.id}
                entry={entry}
                expanded={expandedId === entry.id}
                onToggle={(id) => setExpandedId((curr) => (curr === id ? null : id))}
                showTenantColumn={showTenantColumn}
              />
            ))}
          </tbody>
        </Table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          type="button" className="btn btn-sm btn-ghost-secondary"
          disabled={cursor === null}
          onClick={() => { setCursor(null); setExpandedId(null); }}
        >← Inizio</button>
        <button
          type="button" className="btn btn-sm btn-ghost-secondary"
          disabled={!data?.next_cursor}
          onClick={() => { setCursor(data?.next_cursor ?? null); setExpandedId(null); }}
        >Next →</button>
      </div>
    </div>
  );
}
